// src/pages/MatchesPage.jsx
import { useState } from "react";
import { FaCalendar, FaTrophy, FaUsers } from "react-icons/fa";

// Import match flyers
import TourneyImg from "../assets/matches/tourney.jpg";
import SndImg from "../assets/matches/snd.jpeg";
import OneVOneImg from "../assets/matches/1v1.jpeg";

const tournaments = [
  {
    id: 1,
    title: "Caribbean 4v4 Championship",
    date: "February 2025",
    status: "upcoming",
    prizePool: "$500",
    format: "4v4 CDL",
    game: "Call of Duty: Black Ops 6",
    image: TourneyImg,
    description: "The premier Caribbean 4v4 tournament featuring the best teams from across the region.",
  },
  {
    id: 2,
    title: "Search & Destroy Tournament",
    date: "January 2025",
    status: "completed",
    prizePool: "$250",
    format: "4v4 SnD",
    game: "Call of Duty: Black Ops 6",
    image: SndImg,
    description: "Intense Search & Destroy competition with teams battling for supremacy.",
  },
  {
    id: 3,
    title: "1v1 Championship",
    date: "December 2024",
    status: "completed",
    prizePool: "$100",
    format: "1v1",
    game: "Call of Duty: Black Ops 6",
    image: OneVOneImg,
    description: "Solo tournament showcasing individual skill and gunplay.",
  },
];

const filters = ["All", "Upcoming", "Completed"];

function MatchesPage() {
  const [activeFilter, setActiveFilter] = useState("All");

  const filteredTournaments = activeFilter === "All"
    ? tournaments
    : tournaments.filter(t => t.status === activeFilter.toLowerCase());

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            Tournaments & <span className="text-red-600">Matches</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Stay updated on Prime Valor's competitive journey. From regional showdowns
            to international tournaments.
          </p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-8 px-6 bg-gray-950 border-y border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-red-600">50+</div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Tournaments Played</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-red-600">$5K+</div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Prize Money Won</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-red-600">30+</div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Tournament Wins</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="sticky top-[72px] z-40 bg-gray-950/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex gap-2">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wide transition-all duration-300 ${
                  activeFilter === filter
                    ? "bg-red-600 text-white"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tournaments Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTournaments.map((tournament) => (
              <div
                key={tournament.id}
                className="group bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300"
              >
                {/* Tournament Image */}
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={tournament.image}
                    alt={tournament.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                      tournament.status === "upcoming"
                        ? "bg-green-600 text-white"
                        : "bg-gray-700 text-gray-300"
                    }`}>
                      {tournament.status}
                    </span>
                  </div>
                </div>

                {/* Tournament Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-500 transition-colors">
                    {tournament.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{tournament.description}</p>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FaCalendar className="text-red-600" />
                      <span className="text-gray-300">{tournament.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FaTrophy className="text-red-600" />
                      <span className="text-gray-300">Prize: {tournament.prizePool}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FaUsers className="text-red-600" />
                      <span className="text-gray-300">{tournament.format}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-800">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      {tournament.game}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTournaments.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No tournaments found.</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-6 bg-gradient-to-r from-red-900/20 to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to Compete?
          </h2>
          <p className="text-gray-400 mb-8">
            Join our Discord server to stay updated on upcoming tournaments and register for events.
          </p>
          <a
            href="https://discord.gg/SGXXCMpCxf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
          >
            Join Discord
          </a>
        </div>
      </section>
    </main>
  );
}

export default MatchesPage;
